import React, { useMemo, useEffect, useRef, useState } from 'react'
import SandboxUploadComponent from 'components/utility/sandbox_upload/component'
import { useUploader } from 'components/utility/sandbox_upload/hooks'

export default function ({
  field: { placeholder, system_only, extras },
  id,
  onChange,
  value,
  onBlur,
  className,
  disabled,
}) {
  // Generate a stable uploader UUID
  const uploaderUUID = `form-field-${id}`

  // Get uploader functions from the store
  const { removeAllFiles, addUploadedFiles } = useUploader(uploaderUUID)

  // Internal state to manage all files with their complete data
  // Structure: { fileUUID: { file, metadata, progress, ... } }
  const [filesState, setFilesState] = useState({})

  // Handle changes to external value prop
  useEffect(() => {
    if (!value) {
      // Value is null/undefined - clear store only
      removeAllFiles(false)
      return
    }

    if (typeof value === 'object') {
      // Value is an object (existing files) - add these files to the store
      removeAllFiles(false)

      const fileUUID = value.fileUUID || id
      addUploadedFiles({ [fileUUID]: value })
      // Also update internal state
      setFilesState(value)
    }
  }, [value, removeAllFiles, addUploadedFiles])

  const handleChange = (fileData, fileName) => {
    const updates = { [id]: fileData }
    if (extras.nameId) updates[extras.nameId] = fileName
    onChange(updates)
  }

  const props = {
    disabled,
    placeholder,
    onChange: handleChange,
    accept: extras.accepts || '.pdf',
    onBlur,
  }

  const onFileUpload = (payload) => {
    const { fileUUID, file, progress } = payload

    // Add or update file in internal state
    setFilesState((prev) => ({
      ...prev,
      [fileUUID]: {
        ...prev[fileUUID],
        file: prev[fileUUID]?.file ?? {
          name: file.name,
          size: file.size,
          type: file.type,
        },
        progress: progress ?? prev[fileUUID]?.progress ?? 0,
        fileUUID,
      },
    }))
  }

  const onFileProcessed = (payload) => {
    const { fileUUID, metadata } = payload

    const {
      id,
      md5,
      original_filename,
      original_key,
      pdf_key,
      processing_status,
      created_at,
      urls: {
        original: original_url,
        pdf: pdf_url,
        extra: { expired_at: urls_expired_at },
      },
    } = metadata

    // Update file with complete metadata
    setFilesState((prev) => ({
      ...prev,
      [fileUUID]: {
        ...prev[fileUUID],
        metadata,
        fileUUID,
        processed: true,
      },
    }))

    handleChange({ ...filesState[fileUUID], metadata, fileUUID, processed: true }, original_filename)
  }

  const onFileRemoved = (payload) => {
    const { fileUUID } = payload

    // Remove file from internal state
    // Note: File is already removed from store by the component
    setFilesState((prev) => {
      const newState = { ...prev }
      delete newState[fileUUID]
      return newState
    })

    handleChange(null, null)
  }

  const accepts = useMemo(() => props.accept.split(','), [props.accept])

  // TODO: Handle system_only
  // if (system_only) return <div className={className}></div>

  return (
    <div className={className}>
      <SandboxUploadComponent
        uploaderUUID={uploaderUUID}
        multiple={false}
        accepts={accepts}
        onFileUpload={onFileUpload}
        onFileProcessed={onFileProcessed}
        onFileRemoved={onFileRemoved}
      />
    </div>
  )
}
